import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';

import { formatUsuarioLabel } from '@leadsphere/types';

import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Input } from '../../components/ui/Input.js';
import { SeccionColapsable } from '../../components/ui/SeccionColapsable.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { toDateInputValue } from '../../lib/dates.js';
import type { CatalogoItem, Usuario } from '../clientes/types.js';
import type { CatalogoArea } from '../catalogos/types.js';

// 'root'/'CEO' excluidos a propósito — mismo criterio que UsuarioModal.tsx
// antes de este prompt: ninguno de los dos se asigna desde la UI genérica
// (SRS-01 §9.1; Prompt AF-4(RH) para CEO). El backend igual lo rechaza con
// 403 si se manda.
const ROL_OPTIONS = [
  { value: 'head', label: 'Head' },
  { value: 'analista', label: 'Analista' },
  { value: 'kam', label: 'KAM' },
  // Equivalente exacto de 'kam' en todos los guards (decisión 10-oct).
  { value: 'ads_manager', label: 'Ads manager' },
  { value: 'comercial', label: 'Comercial' },
  // Decisión 15-oct: Ceci, gestora de cartera — escribe en CxC y gestiona clientes.
  { value: 'gerente_comercial', label: 'Gerente Comercial' },
  { value: 'digital', label: 'Digital' },
  // Prompt BJ-04 — equivalente exacto de 'digital' en todos los guards.
  { value: 'marketing', label: 'Marketing' },
] as const;

const TIPO_EMPLEO_OPTIONS = [
  { value: 'tiempo_completo', label: 'Tiempo completo' },
  { value: 'medio_tiempo', label: 'Medio tiempo' },
  { value: 'honorarios', label: 'Honorarios' },
] as const;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

const selectStyle = { padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%' };

// Prompt BB-3(RH) — Pantalla 2: mismo componente para ver/editar un
// Empleado/Usuario existente (:id) y para crear uno nuevo (/usuarios/nuevo),
// mismo patrón ya validado en Días Festivos/Tipos de Ausencia (borrador
// local + Guardar/Cancelar + useBlocker/useBeforeUnload, sin modales).
// Ningún campo nuevo respecto a lo que ya existía en `usuarios` desde AB —
// esto solo expone el resto del formulario que antes solo se podía tocar
// vía escritura directa a BD.
export function UsuarioDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post, patch } = useApi();
  const { user: actor } = useAuth();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const esNuevo = !id;

  const { data: usuario, isLoading } = useQuery({
    queryKey: ['usuarios', id],
    // No existe GET /usuarios/:id — se resuelve desde la lista ya cacheada
    // (mismo patrón que UsuarioModal.tsx usaba antes de este prompt).
    queryFn: async () => {
      const todos = await get<Usuario[]>('/usuarios');
      return todos.find(u => u.id === id) ?? null;
    },
    enabled: !esNuevo,
  });

  const { data: areas } = useQuery({
    queryKey: ['catalogo-area'],
    queryFn: () => get<CatalogoArea[]>('/catalogos/area'),
  });

  // Prompt BJ-10 — catálogo de Puestos (mismo patrón que Área arriba).
  const { data: puestos } = useQuery({
    queryKey: ['catalogo-puesto'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/puesto'),
  });

  // Para los selects de Jefe directo/Aprobador de permisos — cualquier otro
  // usuario real, excluyéndose a sí mismo en modo edición.
  const { data: todosUsuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
  });
  const usuariosSeleccionables = (todosUsuarios ?? []).filter(u => u.id !== id);

  const [nombre, setNombreRaw] = useState('');
  const [correo, setCorreoRaw] = useState('');
  const [rol, setRolRaw] = useState('kam');
  const [puesto, setPuestoRaw] = useState('');
  // Prompt BJ-04 — 100% visual (ver formatUsuarioLabel), root nunca es intern.
  const [esInterno, setEsInternoRaw] = useState(false);
  const [area, setAreaRaw] = useState('');
  const [fechaIngreso, setFechaIngresoRaw] = useState('');
  const [fechaIngresoNomina, setFechaIngresoNominaRaw] = useState('');
  const [fechaNacimiento, setFechaNacimientoRaw] = useState('');
  const [reportsTo, setReportsToRaw] = useState('');
  const [esAprobadorFinal, setEsAprobadorFinalRaw] = useState(false);
  const [aprobadorPermisosId, setAprobadorPermisosIdRaw] = useState('');
  const [tipoEmpleo, setTipoEmpleoRaw] = useState('tiempo_completo');
  const [celular, setCelularRaw] = useState('');
  const [correoPersonal, setCorreoPersonalRaw] = useState('');
  const [contactoNombre, setContactoNombreRaw] = useState('');
  const [contactoTelefono, setContactoTelefonoRaw] = useState('');
  const [contactoRelacion, setContactoRelacionRaw] = useState('');
  const [cargado, setCargado] = useState(false);

  // Mismo bug/fix ya validado en AD-2 (Tipos de Ausencia): flag explícito,
  // reseteado con flushSync ANTES de cualquier navigate() posterior al
  // guardado exitoso — nunca una comparación derivada contra la data cacheada.
  const [isDirty, setIsDirty] = useState(false);

  function marcarDirty() {
    setIsDirty(true);
  }

  const setNombre = (v: string) => { setNombreRaw(v); marcarDirty(); };
  const setCorreo = (v: string) => { setCorreoRaw(v); marcarDirty(); };
  const setRol = (v: string) => { setRolRaw(v); marcarDirty(); };
  const setPuesto = (v: string) => { setPuestoRaw(v); marcarDirty(); };
  const setEsInterno = (v: boolean) => { setEsInternoRaw(v); marcarDirty(); };
  const setArea = (v: string) => { setAreaRaw(v); marcarDirty(); };
  const setFechaIngreso = (v: string) => { setFechaIngresoRaw(v); marcarDirty(); };
  const setFechaIngresoNomina = (v: string) => { setFechaIngresoNominaRaw(v); marcarDirty(); };
  const setFechaNacimiento = (v: string) => { setFechaNacimientoRaw(v); marcarDirty(); };
  const setReportsTo = (v: string) => { setReportsToRaw(v); marcarDirty(); };
  const setEsAprobadorFinal = (v: boolean) => { setEsAprobadorFinalRaw(v); marcarDirty(); };
  const setAprobadorPermisosId = (v: string) => { setAprobadorPermisosIdRaw(v); marcarDirty(); };
  const setTipoEmpleo = (v: string) => { setTipoEmpleoRaw(v); marcarDirty(); };
  const setCelular = (v: string) => { setCelularRaw(v); marcarDirty(); };
  const setCorreoPersonal = (v: string) => { setCorreoPersonalRaw(v); marcarDirty(); };
  const setContactoNombre = (v: string) => { setContactoNombreRaw(v); marcarDirty(); };
  const setContactoTelefono = (v: string) => { setContactoTelefonoRaw(v); marcarDirty(); };
  const setContactoRelacion = (v: string) => { setContactoRelacionRaw(v); marcarDirty(); };

  function cargarDesdeUsuario(u: Usuario) {
    setNombreRaw(u.nombre);
    setCorreoRaw(u.correo);
    setRolRaw(u.rol);
    setPuestoRaw(u.puesto ?? '');
    setEsInternoRaw(u.es_interno);
    setAreaRaw(u.area ?? '');
    setFechaIngresoRaw(toDateInputValue(u.fecha_ingreso));
    setFechaIngresoNominaRaw(toDateInputValue(u.fecha_ingreso_nomina));
    setFechaNacimientoRaw(toDateInputValue(u.fecha_nacimiento));
    setReportsToRaw(u.reports_to ?? '');
    setEsAprobadorFinalRaw(u.es_aprobador_final);
    setAprobadorPermisosIdRaw(u.aprobador_permisos_id ?? '');
    setTipoEmpleoRaw(u.tipo_empleo);
    setCelularRaw(u.celular ?? '');
    setCorreoPersonalRaw(u.correo_personal ?? '');
    setContactoNombreRaw(u.contacto_emergencia_nombre ?? '');
    setContactoTelefonoRaw(u.contacto_emergencia_telefono ?? '');
    setContactoRelacionRaw(u.contacto_emergencia_relacion ?? '');
  }

  useEffect(() => {
    if (esNuevo || !usuario || cargado) return;
    cargarDesdeUsuario(usuario);
    setCargado(true);
  }, [esNuevo, usuario, cargado]);

  const blocker = useBlocker(isDirty);

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (!isDirty) return;
        e.preventDefault();
        e.returnValue = '';
      },
      [isDirty],
    ),
  );

  const [desactivarPendiente, setDesactivarPendiente] = useState(false);

  const toggleActivoMutation = useMutation({
    mutationFn: (activo: boolean) => patch<Usuario>(`/usuarios/${id}/activo`, { activo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      showToast('success', 'Estatus actualizado correctamente.');
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible actualizar el estatus.')),
  });

  const guardarMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        nombre: nombre.trim(),
        rol,
        puesto: puesto.trim() || null,
        // root nunca es intern — se fuerza false sin importar el checkbox
        // (el checkbox ya está oculto/deshabilitado para rol='root' en la UI).
        es_interno: rol === 'root' ? false : esInterno,
        area: area || null,
        fecha_ingreso: fechaIngreso || null,
        fecha_ingreso_nomina: fechaIngresoNomina || null,
        fecha_nacimiento: fechaNacimiento || null,
        reports_to: reportsTo || null,
        es_aprobador_final: esAprobadorFinal,
        aprobador_permisos_id: esAprobadorFinal ? null : (aprobadorPermisosId || null),
        tipo_empleo: tipoEmpleo,
        celular: celular.trim() || null,
        correo_personal: correoPersonal.trim() || null,
        contacto_emergencia_nombre: contactoNombre.trim() || null,
        contacto_emergencia_telefono: contactoTelefono.trim() || null,
        contacto_emergencia_relacion: contactoRelacion.trim() || null,
      };
      return esNuevo
        ? post<Usuario>('/usuarios', { ...payload, correo: correo.trim().toLowerCase() })
        : patch<Usuario>(`/usuarios/${id}`, payload);
    },
    onSuccess: resultado => {
      // flushSync — mismo mecanismo que TipoAusenciaDetalle.tsx (AD-2): sin
      // esto, el blocker seguía viendo isDirty=true en el mismo tick del
      // navigate() de abajo.
      flushSync(() => setIsDirty(false));
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      if (esNuevo) {
        navigate(`/usuarios/${resultado.id}`, { replace: true });
        showToast('success', 'Empleado creado correctamente');
      } else {
        queryClient.invalidateQueries({ queryKey: ['usuarios', id] });
        showToast('success', 'Cambios guardados correctamente');
      }
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible guardar')),
  });

  function handleGuardar() {
    if (!nombre.trim() || !rol) {
      showToast('error', 'Nombre y Rol son obligatorios');
      return;
    }
    if (esNuevo && !/^[^\s@]+@leadsphere\.com$/i.test(correo.trim())) {
      showToast('error', 'El correo debe pertenecer al dominio @leadsphere.com');
      return;
    }
    if (!esAprobadorFinal && !aprobadorPermisosId) {
      showToast('error', 'Elige un Aprobador de permisos, o marca "¿Es aprobador de permisos?"');
      return;
    }
    guardarMutation.mutate();
  }

  function handleCancelar() {
    if (esNuevo) {
      navigate('/usuarios');
      return;
    }
    if (!usuario) return;
    cargarDesdeUsuario(usuario);
    setIsDirty(false);
  }

  const confirmSalida = (
    <ConfirmModal
      open={blocker.state === 'blocked'}
      title="¿Salir sin guardar?"
      body="Tienes cambios sin guardar. Si sales ahora, se perderán."
      confirmLabel="Salir sin guardar"
      danger
      onConfirm={() => blocker.state === 'blocked' && blocker.proceed()}
      onCancel={() => blocker.state === 'blocked' && blocker.reset()}
    />
  );

  if (!esNuevo && (isLoading || !cargado)) {
    return <div>Cargando…</div>;
  }
  if (!esNuevo && !usuario) {
    return <div>Usuario no encontrado.</div>;
  }

  // root/CEO — mismo criterio ya establecido en UsuariosPage.tsx: ninguno de
  // los dos se puede editar/desactivar/eliminar desde la UI. La ficha se ve
  // en solo lectura en vez de simplemente no ser accesible.
  const esProtegido = !esNuevo && (usuario!.rol === 'root' || usuario!.rol === 'CEO');
  const esUnoMismo = !esNuevo && usuario!.id === actor?.sub;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>
          {esNuevo ? 'Nuevo Empleado' : usuario!.nombre}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {!esProtegido && (
            <>
              <Button variant="secondary" onClick={handleCancelar} disabled={!esNuevo && (!isDirty || guardarMutation.isPending)}>
                Cancelar
              </Button>
              <Button onClick={handleGuardar} disabled={!isDirty || guardarMutation.isPending}>
                {guardarMutation.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            </>
          )}
        </div>
      </div>

      {esProtegido && (
        <div className="ff-hint" style={{ marginBottom: 16 }}>
          El usuario {usuario!.rol === 'root' ? 'root' : 'CEO'} no se puede editar, desactivar ni eliminar desde esta pantalla.
        </div>
      )}

      {!esNuevo && !esProtegido && (
        <div className="plat-card" style={{ maxWidth: 640, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--heading)' }}>Estatus</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{usuario!.activo ? 'Activo' : 'Inactivo — no puede iniciar sesión'}</div>
          </div>
          <label className="tog" title={esUnoMismo ? 'No puedes desactivar tu propia cuenta.' : usuario!.activo ? 'Desactivar' : 'Activar'}>
            <input
              type="checkbox"
              checked={usuario!.activo}
              disabled={esUnoMismo}
              onChange={e => (e.target.checked ? toggleActivoMutation.mutate(true) : setDesactivarPendiente(true))}
            />
            <span className="tog-slider" />
          </label>
        </div>
      )}

      <div style={{ maxWidth: 640 }}>
        <SeccionColapsable title="Datos generales" defaultOpen>
          <fieldset disabled={esProtegido} style={{ border: 'none', padding: 0, margin: 0 }}>
            <Input label="Nombre completo *" value={nombre} onChange={e => setNombre(e.target.value)} />
            <Input
              label="Correo *"
              type="email"
              value={correo}
              disabled={!esNuevo}
              placeholder="nombre@leadsphere.com"
              onChange={e => setCorreo(e.target.value)}
            />
            {!esNuevo && <div className="ff-hint" style={{ marginTop: -8 }}>El correo no se puede cambiar — es el identificador de acceso vía Google OAuth.</div>}

            <div className="field">
              <label>Rol *</label>
              <select value={rol} onChange={e => setRol(e.target.value)} style={selectStyle}>
                {ROL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Puesto / Designación</label>
              <select value={puesto} onChange={e => setPuesto(e.target.value)} style={selectStyle}>
                <option value="">Sin puesto</option>
                {/* Prompt BJ-10 — si el puesto ya guardado (ej. usuarios sembrados
                    antes de crear el catálogo) todavía no está en la lista, se
                    inyecta como opción extra para que siga mostrándose seleccionado
                    en vez de aparecer en blanco, hasta que se dé de alta formalmente. */}
                {puesto && !(puestos ?? []).some(p => p.valor === puesto) && (
                  <option value={puesto}>{puesto} (no catalogado)</option>
                )}
                {(puestos ?? []).map(p => (
                  <option key={p.id} value={p.valor}>{p.valor}</option>
                ))}
              </select>
            </div>

            {/* Prompt BJ-04 — 100% visual (ver formatUsuarioLabel), root nunca
                puede ser intern por decisión explícita: oculto cuando rol='root'
                (en la práctica inalcanzable vía este <select>, pero se cubre
                también por defensa en profundidad). */}
            {rol !== 'root' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                <input
                  type="checkbox"
                  checked={esInterno}
                  onChange={e => setEsInterno(e.target.checked)}
                />
                Es intern
              </label>
            )}

            <div className="field">
              <label>Área</label>
              <select value={area} onChange={e => setArea(e.target.value)} style={selectStyle}>
                <option value="">Sin área</option>
                {(areas ?? []).map(a => (
                  <option key={a.id} value={a.nombre}>{a.nombre}</option>
                ))}
              </select>
            </div>

            <Input label="Fecha de ingreso" type="date" value={fechaIngreso} onChange={e => setFechaIngreso(e.target.value)} />
            <Input label="Fecha de ingreso nómina (opcional)" type="date" value={fechaIngresoNomina} onChange={e => setFechaIngresoNomina(e.target.value)} />

            <div className="field">
              <label>Jefe directo</label>
              <select value={reportsTo} onChange={e => setReportsTo(e.target.value)} style={selectStyle}>
                <option value="">Sin jefe directo</option>
                {usuariosSeleccionables.map(u => (
                  <option key={u.id} value={u.id}>{u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre}</option>
                ))}
              </select>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, margin: '4px 0 14px' }}>
              <input type="checkbox" checked={esAprobadorFinal} onChange={e => setEsAprobadorFinal(e.target.checked)} />
              ¿Es aprobador de permisos? (tope del organigrama — nadie le aprueba)
            </label>

            {!esAprobadorFinal && (
              <div className="field">
                <label>Aprobador de permisos *</label>
                <select value={aprobadorPermisosId} onChange={e => setAprobadorPermisosId(e.target.value)} style={selectStyle}>
                  <option value="">Selecciona…</option>
                  {usuariosSeleccionables.map(u => (
                    // Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno.
                    <option key={u.id} value={u.id}>{u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre}</option>
                  ))}
                </select>
                <span className="ff-hint">Quién aprueba sus solicitudes de ausencia — independiente del Jefe directo.</span>
              </div>
            )}

            <Input label="Fecha de nacimiento" type="date" value={fechaNacimiento} onChange={e => setFechaNacimiento(e.target.value)} />

            <div className="field">
              <label>Tipo de empleo</label>
              <select value={tipoEmpleo} onChange={e => setTipoEmpleo(e.target.value)} style={selectStyle}>
                {TIPO_EMPLEO_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </fieldset>
        </SeccionColapsable>

        <SeccionColapsable title="Contacto" defaultOpen>
          <fieldset disabled={esProtegido} style={{ border: 'none', padding: 0, margin: 0 }}>
            <Input label="Celular" value={celular} onChange={e => setCelular(e.target.value)} />
            <Input label="Correo personal" type="email" value={correoPersonal} onChange={e => setCorreoPersonal(e.target.value)} />
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <Input label="Contacto de emergencia — Nombre" value={contactoNombre} onChange={e => setContactoNombre(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <Input label="Teléfono" value={contactoTelefono} onChange={e => setContactoTelefono(e.target.value)} />
              </div>
            </div>
            <Input label="Relación" value={contactoRelacion} placeholder="Ej. Cónyuge, madre, hermano…" onChange={e => setContactoRelacion(e.target.value)} />
          </fieldset>
        </SeccionColapsable>
      </div>

      <ConfirmModal
        open={desactivarPendiente}
        title="¿Desactivar este usuario?"
        body="Su sesión activa se cerrará de inmediato (se revoca su refresh token) y no podrá iniciar sesión hasta que se reactive."
        confirmLabel="Sí, desactivar"
        danger
        onConfirm={() => {
          toggleActivoMutation.mutate(false);
          setDesactivarPendiente(false);
        }}
        onCancel={() => setDesactivarPendiente(false)}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />

      {confirmSalida}
    </div>
  );
}
